/**
 * feat_345 — ¿en qué cola queda un CERRADO que alguien vuelve a tomar?
 *
 * Refina feat_338, que resolvía lo mismo con una sola línea (`myQueueIds[0]`,
 * "la primera cola del que toma") escrita DOS veces: en el REABRIR de
 * `components/TicketActionButtons` y, copiada a mano, en el ATENDER de vista
 * previa de `components/Ticket`. Acá la regla vive UNA sola vez y los dos
 * call-sites la consumen; si vuelve a duplicarse, vuelven a divergir.
 *
 * Por qué el ticket tiene que MUDARSE de cola: sin el movimiento queda "mío
 * pero invisible" — el filtro de colas del listado (back y front) lo saca de
 * todas mis listas y solo se llega por URL directa o buscándolo con la lupa.
 *
 * Tres decisiones, en este orden (el orden importa, ver abajo):
 *
 *  1. Solo aplica al cross-take real: pool en modo "company" (el único que deja
 *     llegar a un cerrado de OTRA cola), transición a "open" y el ticket
 *     viniendo de closed/resolved. ACEPTAR un pending o DEVOLVER a la cola no
 *     tocan la cola nunca.
 *  2. Si el ticket YA era mío —lo resolví yo, o yo era su dueño— CONSERVA su
 *     cola y no se pregunta nada. Es un regreso al propio archivo, no una
 *     apropiación: mudarlo sería mover de cola un ticket que nunca fue ajeno.
 *     Este chequeo va ANTES del de "cola propia" justo porque tiene que ganar
 *     también cuando la cola del ticket es ajena.
 *  3. Cross-take de verdad: 1 cola propia → se usa esa (= feat_338, sin
 *     fricción); varias → lo elige el que toma, con SelectQueueOnAcceptModal.
 *
 * 🔴 DEPENDE DE QUE `resolvedByUserId`, `userId` y `queueId` VENGAN EN EL
 * OBJETO `ticket`. Hoy se sirven solos: ni `ShowTicketService` ni el socket de
 * `UpdateTicketService` recortan con `attributes:[...]` de nivel superior, así
 * que Sequelize devuelve todas las columnas del modelo (hallazgo de feat_185,
 * reverificado en el recon de feat_345). Si alguien agrega un `attributes` a
 * ShowTicketService, esto NO rompe: se degrada en SILENCIO a "todo es
 * cross-take" (siempre pregunta / siempre muda). MANTENER SINCRONIZADO.
 *
 * `Number()` en las dos puntas de cada comparación de ids, mismo patrón que
 * `helpers/canOpenTicket.js`.
 *
 * @param {object} ticket      el ticket (status, userId, resolvedByUserId, queueId)
 * @param {object} user        AuthContext.user (id, queues[])
 * @param {"off"|"queue"|"company"} pooledMode  ya mapeado con pooledModeFromSetting
 * @param {string} status      el estado AL QUE se va ("open" en el reopen/atender)
 * @returns {{mode:"none"}|{mode:"auto",queueId:number}|{mode:"ask",queueIds:number[]}}
 *   none → NO mandar `queueId` en el PUT (undefined conserva la cola actual;
 *          mandar null dejaría el ticket huérfano de cola, peor que no tocarlo)
 *   auto → mandar ese `queueId` directo
 *   ask  → abrir el modal con esas colas y re-entrar con la elegida
 */
export const resolveTakeoverQueue = ({ ticket, user, pooledMode, status }) => {
  const wasClosed =
    ticket?.status === "closed" || ticket?.status === "resolved";
  if (pooledMode !== "company" || status !== "open" || !wasClosed) {
    return { mode: "none" };
  }

  const myQueueIds = (user?.queues || []).map(q => q.id);
  // Sin colas propias no hay nada que elegir ni adónde mudarlo.
  if (myQueueIds.length === 0) return { mode: "none" };

  const uid = Number(user?.id);
  // "Mío" = lo resolví yo O era mi dueño. El segundo término cubre los cierres
  // AUTOMÁTICOS (jobs / auto-close / legacy pre-feat_185), que dejan
  // `resolvedByUserId` en null — el 45% de los cerrados de co1 — y que sin él
  // se tratarían como ajenos aunque el ticket fuera mío.
  const isMine =
    Number(ticket?.resolvedByUserId) === uid || Number(ticket?.userId) === uid;
  if (isMine) return { mode: "none" };

  // Cross-take pero el ticket ya está en una cola mía: se queda donde está,
  // igual va a aparecer en mis listas.
  if (ticket?.queueId && myQueueIds.includes(ticket.queueId)) {
    return { mode: "none" };
  }

  // Cross-take real: cola ajena, o SIN cola (un cerrado huérfano de cola es
  // justo el que más conviene clasificar a mano).
  if (myQueueIds.length === 1) return { mode: "auto", queueId: myQueueIds[0] };
  return { mode: "ask", queueIds: myQueueIds };
};

export default resolveTakeoverQueue;
